<template>
  <div>
    <!-- Breadcrumb -->
    <app-breadcrumb>
      <template #contentright>
        <span />
      </template>
    </app-breadcrumb>

    <!-- Filters -->
    <b-card no-body>
      <b-card-header class="pb-50">
        <h5>
          {{ $t('Filters') }}
        </h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col
            cols="12"
            md="4"
            class="mb-md-0 mb-2"
          >
            <label>Người dùng</label>
            <v-select
              v-model="searchUserId"
              label="name"
              :filterable="false"
              :options="userOptions"
              @search="fetchUsers"
            >
              <template slot="no-options">
                tìm user...
              </template>
              <template
                slot="option"
                slot-scope="option"
              >
                <div class="d-center">
                  {{ option.username }} ({{ option.full_name }})
                </div>
              </template>
              <template
                slot="selected-option"
                slot-scope="option"
              >
                <div class="selected d-center">
                  {{ option.username }} ({{ option.full_name }})
                </div>
              </template>
            </v-select>

          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mb-md-0 mb-2"
          >
            <label>Từ ngày</label>
            <b-input-group class="input-group-merge">
              <flat-pickr
                v-model="startDate"
                class="form-control"
                :config="{ disableMobile: true }"
              />
              <b-input-group-append is-text>
                <feather-icon icon="CalendarIcon" />
              </b-input-group-append>
            </b-input-group>

          </b-col>
          <b-col
            cols="12"
            md="4"
            class="mb-md-0 mb-2"
          >
            <label>Đến ngày</label>
            <b-input-group class="input-group-merge">
              <flat-pickr
                v-model="endDate"
                class="form-control"
                :config="{ disableMobile: true }"
              />
              <b-input-group-append is-text>
                <feather-icon icon="CalendarIcon" />
              </b-input-group-append>
            </b-input-group>

          </b-col>

        </b-row>
      </b-card-body>
    </b-card>

    <!-- Table Container Card -->
    <b-overlay
      :show="loading"
      spinner-variant="primary"
      spinner-type="grow"
      spinner-small
      rounded="sm"
    >
      <b-card
        no-body
        class="mb-0"
      >

        <div class="m-2">
          <!-- Table Top -->
          <b-row>
            <!-- Per Page -->
            <b-col
              cols="12"
              md="6"
              class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
            >
              <label>{{ $t('Show') }}</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block mx-50"
              />
              <label>{{ $t('entries') }}</label>
            </b-col>

            <!-- Search -->
            <b-col
              cols="12"
              md="6"
            >
              <div class="d-flex align-items-center justify-content-end" />
            </b-col>
          </b-row>
        </div>

        <b-table
          ref="refListTable"
          class="position-relative"
          :items="itemsData"
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          responsive
          show-empty
          empty-text="Không tìm thấy kết quả"
          :sort-desc.sync="isSortDirDesc"
        >

          <!-- Column: user_id -->
          <template #cell(user)="data">
            <b-link
              :to="{ name: 'users-view', params: { id: data.item.user.id } }"
              class="font-weight-bold d-block text-nowrap"
            >
              {{ data.item.user.full_name }}
            </b-link>
            <small class="text-muted">@{{ data.item.user.username }}</small>
          </template>

          <template #cell(module)="data">
            {{ $t(data.item.module) }}
          </template>
          <template #cell(device)="data">
            {{ deviceType(data.item.ua) }}
          </template>
          <template #cell(browser)="data">
            {{ getBrowser(data.item.ua) }}
          </template>
        </b-table>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-start"
            >
              <span class="text-muted">{{ $t('show_to_entries', { from: dataMeta.from, to: dataMeta.to, of: dataMeta.of }) }}</span>
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              sm="6"
              class="d-flex align-items-center justify-content-center justify-content-sm-end"
            >

              <b-pagination
                v-model="currentPage"
                :total-rows="totalEntries"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>
    </b-overlay>
  </div>
</template>

<script>
import {
  BOverlay, BCard, BCardHeader, BCardBody, BRow, BCol, BTable, BInputGroup, BInputGroupAppend,
  BPagination, BLink,
} from 'bootstrap-vue'
import flatPickr from 'vue-flatpickr-component'
import vSelect from 'vue-select'
import { ref, computed, watch } from '@vue/composition-api'
import AppBreadcrumb from '@core/layouts/components/AppBreadcrumb.vue'
import store from '@/store'

export default {
  components: {
    AppBreadcrumb,
    BOverlay,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BTable,
    BLink,
    BInputGroup,
    BInputGroupAppend,
    BPagination,
    flatPickr,
    vSelect,
  },

  setup() {
    // Table Handlers
    const tableColumns = [
      { key: 'user', sortable: false, thAttr: { width: '250' } },
      {
        key: 'module', sortable: false, label: 'Thành phần', thAttr: { width: '100' },
      },
      { key: 'api', sortable: false, label: 'Trang' },
      {
        key: 'logdate', sortable: false, label: 'Ngày truy cập', thAttr: { width: '250' },
      },
      {
        key: 'ip', sortable: false, label: 'Ip truy cập', thAttr: { width: '150' },
      },
      {
        key: 'device', sortable: false, label: 'Thiết bị', thAttr: { width: '150' },
      },
      {
        key: 'browser', sortable: false, label: 'Trình duyệt', thAttr: { width: '150' },
      },
    ];
    const loading = ref(false);
    const perPage = ref(10);
    const totalEntries = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchUserId = ref(0);
    const searchQuery = ref('');
    const sortBy = ref('id');
    const isSortDirDesc = ref(true);
    const isPaginate = ref(1);
    const itemsData = ref([]);
    const startDate = ref(null);
    const endDate = ref(null);
    const refListTable = ref(null);
    const userOptions = ref([]);

    const resolveTypeVariant = (type) => (type === 'login' ? 'success' : 'warning')

    const getBrowser = (ua) => {
      if (ua && ua.length) {
        if ((ua.indexOf('OPR')) !== -1) {
          return 'Opera';
        } if (ua.indexOf('Edg') > -1) {
          return 'Edge';
        } if (ua.indexOf('Chrome') !== -1) {
          return 'Chrome';
        } if (ua.indexOf('Safari') !== -1) {
          return 'Safari';
        } if (ua.indexOf('Firefox') !== -1) {
          return 'Firefox';
        } if ((ua.indexOf('MSIE') !== -1) || (!!document.documentMode === true) || !!ua.match(/Trident.*rv\:11\./)) // IF IE > 10
        {
          return 'IE';
        }
      }
      return 'Unknown';
    }
    const deviceType = (ua) => {
      if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'Tablet';
      }
      if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        return 'Mobile';
      }
      return 'Desktop';
    };
    const fetchLog = () => {
      loading.value = true;
      store.dispatch('logs/fetchArticleLog', {
        user_id: searchUserId.value ? searchUserId.value.id : '',
        start_date: startDate.value,
        end_date: endDate.value,
        perPage: perPage.value,
        page: currentPage.value,
        isPaginate: isPaginate.value,
      })
        .then((response) => {
          loading.value = false;
          if (response.status === 200) {
            const items = response.data.data.data;
            const { total } = response.data.data;
            totalEntries.value = total;
            itemsData.value = items;
          }
        });
    };

    const fetchUsers = (search, fetchLoading) => {
      if (search.length) {
        fetchLoading(true);
        store.dispatch('users/searchUser', { keyword: search })
          .then((res) => {
            if (res.status === 200) {
              userOptions.value = res.data.data;
            } else {
              userOptions.value = [];
            }
            fetchLoading(false)
          })
      }
    };

    watch([currentPage, perPage, searchQuery, searchUserId, startDate, endDate], () => {
      fetchLog();
    });
    const dataMeta = computed(() => {
      const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
      return {
        from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
        to: perPage.value * (currentPage.value - 1) + localItemsCount,
        of: totalEntries.value,
      };
    });

    fetchLog();

    return {
      startDate,
      endDate,
      dataMeta,
      loading,
      tableColumns,
      perPage,
      currentPage,
      totalEntries,
      perPageOptions,
      searchUserId,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refListTable,
      itemsData,
      userOptions,
      fetchLog,
      fetchUsers,
      resolveTypeVariant,
      getBrowser,
      deviceType,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
